import { useState } from "react";
import {
  FloatingPortal,
  autoPlacement,
  autoUpdate,
  useFloating,
  useHover,
  useInteractions,
} from "@floating-ui/react";
import { useRootContext } from "~/context/rootContext";

export type GremlinProp = {
  name: string;
  imageUrl: string;
  thumbnailUrl?: string;
  author?: string;
  owner?: string;
  wishlister?: string;
  character?: string;
  lastAcquired?: string;
  createdAt?: string;

  zoomable?: boolean;
  count?: number;
};

export function Gremlin({
  owner,
  name,
  imageUrl,
  thumbnailUrl,
  author,
  zoomable,
  count,
  character,
  wishlister,
  lastAcquired,
  createdAt,
}: GremlinProp) {
  const { imageBaseUrl } = useRootContext();
  const [isOpen, setIsOpen] = useState(false);

  const {
    refs: hoverRefs,
    floatingStyles: hoverFloatingStyles,
    context: hoverContext,
  } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [autoPlacement()],
    whileElementsMounted: autoUpdate,
  });
  const hover = useHover(hoverContext, {
    restMs: 50,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  return (
    <div className="relative h-full font-card">
      <div className="bg-card flex h-full min-w-0 flex-col gap-y-4 rounded-lg border p-4 border-purple">
        <img
          ref={hoverRefs.setReference}
          alt=""
          className={`my-auto self-center rounded-sm object-contain max-w-24`}
          src={
            thumbnailUrl
              ? `${imageBaseUrl}/${thumbnailUrl}`
              : `${imageBaseUrl}/${imageUrl}`
          }
          srcSet={`${imageBaseUrl}/${thumbnailUrl} 1x, ${imageBaseUrl}/${imageUrl} 2x`}
          {...getReferenceProps()}
        />
        {zoomable && isOpen ? (
          <FloatingPortal>
            <img
              ref={hoverRefs.setFloating}
              style={hoverFloatingStyles}
              className="z-10 min-w-[320px] w-2/6 h-2/6 max-w-min p-4 object-contain self-center backdrop-blur-sm rounded-md border"
              {...getFloatingProps()}
              src={`${imageBaseUrl}/${imageUrl}`}
              alt=""
              loading="lazy"
            />
          </FloatingPortal>
        ) : null}
        <div className="flex flex-col">
          <div className="mb-2 h-[px] w-full bg-white/90"></div>
          <p className="line-clamp-2 font-semibold hover:line-clamp-none">{name}</p>
          {character ? (
            <p className="my-2 text-sm italic line-clamp-2 hover:line-clamp-none">
              {'"'}
              {character}
              {'"'}
            </p>
          ) : null}
          {author ? (
            <p className="text-sm line-clamp-2">
              Made by <span className="font-semibold">{author}</span>
            </p>
          ) : null}
          {owner ? (
            <p className="text-base w-max max-w-full text-wrap">
              Belongs to <span className="font-semibold text-wrap">{owner}</span>
            </p>
          ) : (
            <></>
          )}
          {wishlister ? (
            <p className="text-base w-max max-w-full text-wrap">
              Wishlisted by <span className="font-semibold">{wishlister}</span>
            </p>
          ) : (
            <></>
          )}
          {lastAcquired ? (
            <p className="text-sm">
              Acquired at{" "}
              <span className="font-bold">
                {new Date(lastAcquired).toLocaleDateString("en-US", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}{" "}
              </span>
            </p>
          ) : null}
          {createdAt ? (
            <p className="text-sm mt-1">
              Created at{" "}
              <span className="font-bold">
                {new Date(createdAt).toLocaleDateString("en-US", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}{" "}
              </span>
            </p>
          ) : null}
          {count ? (
            <p className="-mb-2 ml-auto mt-2 w-max font-counter text-2xl font-semibold">
              X{count}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
